import "./ReleaseEvaluationSelector.less";

import { useStrings } from "features/i18n/useStrings";
import strings from "./releaseEvaluationSelector.strings";
import {
    GuxDropdown,
    GuxFormFieldDropdown,
    GuxListbox,
    GuxOption,
} from "genesys-spark-components-react";
import { toGuxDisabled } from "util/componentUtil";
import { useContext, useEffect, useMemo, useState } from "react";
import { DateTime } from "features/i18n/DateTime";
import CustomTimePicker from "./CustomTimePicker";
import { useAppSelector } from "app/hooks";
import {
    selectHasReleasePermissions,
    selectIsScorableComplete,
    selectReleaseDate,
} from "features/scorable/scorableSlice";
import { useResolvedLocale } from "features/i18n/useLocale";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";
import { ReleaseDateOptions } from "enums/ReleaseDateOptions";
import { SubmitReleaseDateButton } from "features/qualityForm/formViewer/footer/ScorableButtons";
import { HasEditScoreOrEditPermissionsContext } from "features/qualityForm/common/Contexts";
import React from "react";
import moment from "moment-timezone";
import { useTimeZone } from "features/timezone/useTimeZone";

const defaultTimeZoneName = moment.tz.guess();

const orderedReleaseDateOptions = [
    ReleaseDateOptions.NOW,
    ReleaseDateOptions.EXISTING,
    ReleaseDateOptions.ENDOFDAY,
    ReleaseDateOptions.ENDOFWEEK,
    ReleaseDateOptions.CUSTOM,
    ReleaseDateOptions.NEVER,
];

function ReleaseTimeOption({
    name,
    time,
    options,
}: {
    name: string;
    time?: Date;
    options: Intl.DateTimeFormatOptions;
}) {
    return (
        <div
            className="ReleaseTimeOption"
            data-testid="ReleaseTimeOption"
            dir="auto"
        >
            <div data-testid="release-time-option-label">{name}</div>
            {time && (
                <div className="release-time">
                    {/* If we use GuxDateTimeBeta here, the value does not show up in the collapsed GuxDropdown */}
                    {/* So the existing component is used for now. The fix for GuxDropdown might be added later. COMUI-2335 */}
                    <DateTime date={time} dateTimeFormatOptions={options} />
                </div>
            )}
        </div>
    );
}

export interface ReleaseDateSelectedType {
    type: ReleaseDateOptions;
    time?: Date;
}

function getEndOfTimeUnitDateTime(
    referenceDateTime: Date,
    timeZoneId: string | undefined,
    timeUnit: "day" | "week"
) {
    const endOfUnitTime =
        timeZoneId === undefined || timeZoneId === defaultTimeZoneName
            ? moment(referenceDateTime).endOf(timeUnit)
            : moment(referenceDateTime).tz(timeZoneId).endOf(timeUnit);
    return new Date(endOfUnitTime.valueOf());
}

export function getCurrentReleaseDate(
    releaseDateData: ReleaseDateSelectedType,
    timeZoneId: string | undefined
) {
    const curr = new Date();
    switch (releaseDateData.type) {
        case ReleaseDateOptions.CUSTOM:
        case ReleaseDateOptions.EXISTING:
            return releaseDateData.time;
        case ReleaseDateOptions.NEVER:
            return undefined;
        case ReleaseDateOptions.NOW:
            return curr;
        case ReleaseDateOptions.ENDOFDAY:
            return getEndOfTimeUnitDateTime(curr, timeZoneId, "day");
        case ReleaseDateOptions.ENDOFWEEK:
            return getEndOfTimeUnitDateTime(curr, timeZoneId, "week");
    }
}

export default function ReleaseEvaluationSelector({
    setReleaseDateData,
    releaseDateData,
    rescoreMode,
    showSubmit,
}: {
    setReleaseDateData: (s: ReleaseDateSelectedType) => void;
    releaseDateData: ReleaseDateSelectedType;
    rescoreMode: boolean;
    showSubmit: boolean;
}) {
    const t = useStrings(strings);
    const locale = useResolvedLocale();

    const isScorableComplete = useAppSelector(selectIsScorableComplete);
    const hasReleasePermissions = useAppSelector(selectHasReleasePermissions);
    const { hasEditScoreOrEditPermissions } = useContext(
        HasEditScoreOrEditPermissionsContext
    );

    const timeZone = useTimeZone();

    const { eodTime, eowTime } = useMemo(() => {
        return {
            eodTime: getCurrentReleaseDate(
                {
                    type: ReleaseDateOptions.ENDOFDAY,
                },
                timeZone
            ),
            eowTime: getCurrentReleaseDate(
                {
                    type: ReleaseDateOptions.ENDOFWEEK,
                },
                timeZone
            ),
        };
    }, []);
    const initialReleaseDate = useAppSelector(selectReleaseDate);

    const [releaseEvaluationOptions, setReleaseEvaluationOptions] = useState<
        Record<ReleaseDateOptions, { name: string; time?: Date }>
    >({
        [ReleaseDateOptions.NOW]: { name: t.labels.releaseEvaluationNow },
        [ReleaseDateOptions.ENDOFDAY]: {
            name: t.labels.releaseEvaluationEOD,
            time: eodTime,
        },
        [ReleaseDateOptions.ENDOFWEEK]: {
            name: t.labels.releaseEvaluationEOW,
            time: eowTime,
        },
        [ReleaseDateOptions.CUSTOM]: { name: t.labels.releaseEvaluationCustom },
        [ReleaseDateOptions.NEVER]: { name: t.labels.releaseEvaluationNever },
        [ReleaseDateOptions.EXISTING]: {
            name: t.labels.releaseEvaluationExistingTime,
            time: undefined,
        },
    });

    const [existingReleaseDate, setExistingReleaseDate] = useState<
        Date | undefined
    >(undefined);

    useEffect(() => {
        setExistingReleaseDate(initialReleaseDate);
    }, [initialReleaseDate]);

    const [releaseDateLabel, setReleaseDateLabel] = useState("");

    const isEvaluationAssigneeFeatureEnabled = useAppSelector((state) => {
        return selectFeatureToggle(
            state,
            FeatureToggle.EVALUATION_ASSIGNEE_FEATURE_TOGGLE_NAME
        );
    });

    const options: Intl.DateTimeFormatOptions = useMemo(() => {
        return {
            day: "numeric",
            month: "long",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            timeZoneName: "short",
            timeZone: timeZone,
        };
    }, [timeZone]);

    function updateReleaseDateLabel(releaseDate: Date | undefined) {
        const dateTimeNow = new Date();
        if (releaseDate !== undefined) {
            if (releaseDate.valueOf() > dateTimeNow.valueOf()) {
                setReleaseDateLabel(
                    t.labels.futureReleaseDate({
                        releaseDate: releaseDate.toLocaleString(
                            locale,
                            options
                        ),
                    })
                );
            } else {
                setReleaseDateLabel(
                    t.labels.pastReleaseDate({
                        releaseDate: releaseDate.toLocaleString(
                            locale,
                            options
                        ),
                    })
                );
            }
        } else {
            setReleaseDateLabel(t.labels.noReleaseDate);
        }
        setReleaseEvaluationOptions({
            ...releaseEvaluationOptions,
            [ReleaseDateOptions.EXISTING]: {
                ...releaseEvaluationOptions.existing,
                time: releaseDate,
            },
        });
    }

    let showDropdown = true;
    if (isEvaluationAssigneeFeatureEnabled) {
        if (isScorableComplete) {
            showDropdown = hasReleasePermissions;
        } else {
            showDropdown =
                hasReleasePermissions && !!hasEditScoreOrEditPermissions;
        }
    }

    useEffect(() => {
        if (rescoreMode || showSubmit) {
            // NOTE: This block is reachable when the evaluation is Complete or In Review
            let defaultReleaseDateType: ReleaseDateOptions;
            if (isEvaluationAssigneeFeatureEnabled) {
                defaultReleaseDateType =
                    existingReleaseDate === undefined
                        ? ReleaseDateOptions.NEVER
                        : ReleaseDateOptions.EXISTING;
            } else {
                defaultReleaseDateType =
                    existingReleaseDate === undefined
                        ? ReleaseDateOptions.NOW
                        : ReleaseDateOptions.EXISTING;
            }
            setReleaseDateData({
                type: defaultReleaseDateType,
                time: existingReleaseDate,
            });
            updateReleaseDateLabel(existingReleaseDate);
        } else {
            // NOTE: This block is for Pending or In Progress evaluations

            // setting the default release time to NOW if rescore mode is OFF and show submit is off
            setReleaseDateData({
                type: ReleaseDateOptions.NOW,
                time: releaseDateData?.time,
            });
            if (isEvaluationAssigneeFeatureEnabled) {
                if (showDropdown) {
                    setReleaseDateLabel("");
                } else {
                    setReleaseDateData({
                        type: ReleaseDateOptions.NEVER,
                        time: undefined,
                    });
                    updateReleaseDateLabel(undefined);
                }
            }
        }
    }, [
        rescoreMode,
        showSubmit,
        existingReleaseDate,
        showDropdown,
        isEvaluationAssigneeFeatureEnabled,
    ]);

    const showExistingReleaseDate =
        (rescoreMode || showSubmit) && existingReleaseDate !== undefined;

    // Once assignee FT is enabled globally and removed, this will be unused and should be removed.
    // So the disabled attributes used below will also need to be removed.
    const isDropdownDisabled =
        !isEvaluationAssigneeFeatureEnabled && !hasEditScoreOrEditPermissions;

    return (
        <div className="ReleaseEvaluationSelector">
            {isEvaluationAssigneeFeatureEnabled &&
                (showDropdown || releaseDateLabel) && (
                    <div
                        className="ReleaseEvaluationLabel"
                        data-testid="label-release-evaluation"
                    >
                        {t.labels.release}
                    </div>
                )}
            {showDropdown && (
                <GuxFormFieldDropdown
                    label-position="above"
                    data-testid="dropdown-release-evaluation-container"
                >
                    <GuxDropdown
                        filterType="starts-with"
                        onInput={(e) => {
                            const releaseValue = (e.target as HTMLInputElement)
                                .value;
                            setReleaseDateData({
                                type: releaseValue as ReleaseDateOptions,
                                time: releaseEvaluationOptions[releaseValue]
                                    .time,
                            });
                        }}
                        value={releaseDateData.type}
                        disabled={toGuxDisabled(isDropdownDisabled)}
                        aria-disabled={toGuxDisabled(isDropdownDisabled)}
                        data-testid="release-date-dropdown"
                    >
                        <GuxListbox aria-label={t.labels.releaseOptions}>
                            {orderedReleaseDateOptions.map((key) => {
                                return showExistingReleaseDate ||
                                    key !== ReleaseDateOptions.EXISTING ? (
                                    <GuxOption
                                        className="release-date-dropdown-label"
                                        data-testid={`release-date-option-${key}`}
                                        value={key}
                                        key={key}
                                    >
                                        <ReleaseTimeOption
                                            name={
                                                releaseEvaluationOptions[key]
                                                    .name
                                            }
                                            time={
                                                releaseEvaluationOptions[key]
                                                    .time
                                            }
                                            options={options}
                                        />
                                    </GuxOption>
                                ) : (
                                    <> </>
                                );
                            })}
                        </GuxListbox>
                    </GuxDropdown>
                    <label slot="label">{t.labels.releaseEvaluation}</label>
                </GuxFormFieldDropdown>
            )}
            {releaseDateLabel && (
                <div
                    className="release-date-label gux-small-text"
                    data-testid="current-release-date-label"
                    dir="auto"
                >
                    {releaseDateLabel}
                </div>
            )}
            {releaseDateData.type === ReleaseDateOptions.CUSTOM && (
                <CustomTimePicker
                    releaseDateData={releaseDateData}
                    setReleaseDateData={setReleaseDateData}
                    timeZoneName={timeZone}
                />
            )}
            <React.Suspense fallback={null}>
                <div hidden={true}>
                    <CustomTimePicker
                        releaseDateData={releaseDateData}
                        setReleaseDateData={setReleaseDateData}
                        timeZoneName={timeZone}
                    />
                </div>
            </React.Suspense>
            {hasReleasePermissions && showSubmit && (
                <SubmitReleaseDateButton
                    releaseDateData={releaseDateData}
                    updateReleaseDateLabel={updateReleaseDateLabel}
                    setExistingReleaseDate={setExistingReleaseDate}
                />
            )}
        </div>
    );
}
